import { fbConversion } from 'api/analytics/analytics'
import { useFetchClient } from 'api/queries/useFetchClient'
import { Button } from 'components/Button'
import { Checkbox } from 'components/Checkbox'
import { Link } from 'components/Link'
import { RadioGroup } from 'components/RadioGroup'
import { Stack } from 'components/Stack'
import { Text } from 'components/Text'
import { TextInput } from 'components/TextInput'
import { Select } from 'components/Select'
import { Loader } from 'features/components/Loader'
import {
  PetAttempt,
  PetAttemptStep3,
  useSubmitPetAttempt,
  determineFeedingProductType,
  isFreezeDried,
} from 'features/MealPlan/api/mutations/useSubmitPetAttempt'
import { useCheckEmail } from 'features/MealPlan/api/queries/useCheckEmail'
import { useFetchPetAttempt } from 'features/MealPlan/api/queries/useFetchPetAttempt'
import { MealPlanLayout } from 'features/MealPlan/components/MealPlanLayout'
import {
  MealPlanQuestionnaireCard,
  MealPlanFormContainer,
} from 'features/MealPlan/components/MealPlanLayout/MealPlanLayout.styles'
import { useRouter } from 'hooks/useRouter'
import { useTranslation } from 'hooks/useTranslation'
import { EatingHabits, HealthGoals, HealthGoalsRanked, Proteins } from 'models/Pet'
import Trans from 'next-translate/Trans'
import React, { ChangeEvent, useEffect, useState } from 'react'
import { debounce } from 'utils/debounce'
import { Box } from '../../../../components/Box'
import { Icon } from '../../../../components/Icon'
import { RankedDragAndDropList } from '../../../../components/RankedDragAndDropList'
import { Tooltip } from '../../../../components/Tooltip'
import { useToast } from '../../../../contexts/ToastContext'
import { Storage, StorageKeys } from '../../../../services/Storage'
import { TextRadioOption } from '../Step1/components/TextRadioOption'
import {
  BenefitsBox,
  BenefitsBoxPlusIcon,
  CTAContainer,
  EmailContainer,
  MealPlanSubscription,
  PrimaryGoal,
  RemoveButtonWrapper,
  Step3Root,
  TermsAndConditionsWrapper,
  TitleContainer,
} from './Step3.styles'

import chickenIcon from '../../../../../public/assets/recipes/chickeniconblack.svg'
import cowIcon from '../../../../../public/assets/recipes/cowiconblack.svg'
import duckIcon from '../../../../../public/assets/recipes/duckiconblack.svg'
import lambIcon from '../../../../../public/assets/recipes/lambiconblack.svg'
import turkeyIcon from '../../../../../public/assets/recipes/turkeyiconblack.svg'
import venisonIcon from '../../../../../public/assets/recipes/venisoniconblack.svg'
import { ENV } from '../../../../constants/EnvHelp'
import { BoxType } from '../../../../models/Box'
import { LoadingInterstitial } from './LoadingInterstitial'
import { FooterMessage } from 'features/MealPlan/components/FooterMessage'
import smallDog from '../../../../../public/assets/icons/small-dog.svg'
import { DietPrior } from 'models/Pet'
import { RawFeedingProductType } from 'models/Product'

type Step3Props = {}

export const Step3 = (_props: Step3Props) => {
  const getFlags = ENV.getVersionFlags()
  const flexBox = getFlags?.flexBox === '04_24_flexBox_v1'
  const flexBoxButtons = getFlags?.flexBox === '04_24_flexBox_v2'
  const creationConfiguration = 'NEW_CONFIG_REV5'

  const { t } = useTranslation()
  const router = useRouter()
  const { addToast, removeToast } = useToast()
  const [popupOpen, setPopupOpen] = useState<string>('cv-1-2-popup-closed')
  const [allergySelected, setAllergySelected] = useState(false)
  const [allergyInfo, setAllergyInfo] = useState({
    hasAllergies: false,
    initialLoadFinished: false,
  })
  const [healthGoalCheckboxes, setHealthGoalCheckboxes] = useState<any[]>([])
  const [selectedHealthGoals, setSelectedHealthGoals] = useState<any[]>([])
  const [flexBoxOption, setFlexBoxOption] = useState<string>('recommended')
  const allHealthGoals = [
    {
      label: t('meal-plan:step-3.ranked-weight-management'),
      name: HealthGoals.WEIGHT_MANAGEMENT,
      id: HealthGoals.WEIGHT_MANAGEMENT,
    },
    {
      label: t('meal-plan:step-3.ranked-longevity'),
      name: HealthGoals.INCREASED_LONGEVITY,
      id: HealthGoals.INCREASED_LONGEVITY,
    },
    {
      label: t('meal-plan:step-3.ranked-heatlhy-digestion'),
      name: HealthGoals.DIGESTION_IMPROVEMENT,
      id: HealthGoals.DIGESTION_IMPROVEMENT,
    },
    {
      label: t('meal-plan:step-3.ranked-healthier-coat'),
      name: HealthGoals.HEALTHIER_COAT,
      id: HealthGoals.HEALTHIER_COAT,
    },
    {
      label: t('meal-plan:step-3.ranked-mealtime-excitement'),
      name: HealthGoals.MEALTIME_EXCITEMENT,
      id: HealthGoals.MEALTIME_EXCITEMENT,
    },
  ]

  const { data: petAttemptStep3, status: petAttemptStatus } = useFetchPetAttempt(
    {
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        const cleanedHealthGoals = data.healthGoals?.filter((healthGoal) =>
          Object.values(HealthGoalsRanked).includes(healthGoal as any)
        )

        if (data.email) {
          setStep3({ ...step3, email: data.email, healthGoals: cleanedHealthGoals })
          setAllergyInfo({
            ...allergyInfo,
            hasAllergies: data.allergies?.length > 0,
            initialLoadFinished: true,
          })
        }
        if (data?.phoneNumber) {
          setStep3({ ...step3, phoneNumber: data.phoneNumber })
        }
      },
    },
    'step3'
  )

  const { data: client, status: clientStatus } = useFetchClient({
    retry: false,
    refetchOnWindowFocus: false,
    onSuccess: (client) => {
      if (client.email) {
        setStep3({
          ...step3,
          email: client.email,
        })
        setTermsAndConditionsAcceptance(true)
      }
    },
  })

  const { submitPetAttempt } = useSubmitPetAttempt()

  const [termsAndConditionsAcceptance, setTermsAndConditionsAcceptance] = useState(false)
  const [loading, setLoading] = useState(true)

  const [step3, setStep3] = useState<Partial<PetAttemptStep3>>({
    allergies: [],
  })

  const { data: emailCheck } = useCheckEmail(step3.email, { keepPreviousData: true })

  const allFieldsFilled = Boolean(
    (client?.email || step3.email) && termsAndConditionsAcceptance && step3.dietPrior
  )

  useEffect(() => {
    if (petAttemptStatus === 'loading' || clientStatus === 'loading') return
    setLoading(false)

    if (client) {
      setStep3({ ...step3, email: client.email })
    }

    if (!petAttemptStep3) return

    setStep3({ ...step3, ...petAttemptStep3 })

    if (healthGoalCheckboxes.length === 0 && step3) {
      const shuffledHealthGoals = allHealthGoals.sort(() => Math.random() - 0.5)
      setHealthGoalCheckboxes(shuffledHealthGoals)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [petAttemptStatus, client, clientStatus, petAttemptStep3])

  useEffect(() => {
    setSelectedHealthGoals(
      step3.healthGoals
        ? step3.healthGoals
            .map((goal) => {
              const foundGoal = allHealthGoals.find((g) => g.id === goal)
              return foundGoal
            })
            .filter((goal) => goal)
        : []
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step3.healthGoals, healthGoalCheckboxes])

  const petName = petAttemptStep3?.name || 'your pet'

  if (loading) return <Loader />

  const handleAllergiesCheckbox = (e: any) => {
    const getAllergies = () => {
      if (!step3.allergies) {
        return [e.target.name]
      }

      if (step3.allergies.includes(e.target.name)) {
        return step3.allergies.filter((allergy: Proteins) => allergy !== e.target.name)
      }

      return [...step3.allergies, e.target.name]
    }

    setStep3({
      ...step3,
      allergies: getAllergies(),
    })
  }

  const onEmailChange = (email: string) => {
    debounce(setStep3, 400)({ ...step3, email })
  }

  const onPhoneNumberChange = (phoneNumber: string) => {
    const numbersOnly = phoneNumber.replace(/\D/g, '')
    if (numbersOnly[0] === '1') {
      return setStep3({ ...step3, phoneNumber: numbersOnly.substring(1) })
    }
    return setStep3({ ...step3, phoneNumber: numbersOnly })
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!allFieldsFilled) return

    const submitData = step3

    if (!allergyInfo.hasAllergies) {
      submitData.allergies = []
    }

    submitData.healthIssues = []

    //update the mealPlan to not include proteins that are in the allergies list
    //this prevents selecting a protein that is in the allergies list on the next step
    if (allergiesTooStrict(submitData.allergies)) return

    let proteinSelectionFix: Proteins[] | Proteins = []
    let initialRawFeedingProductType = RawFeedingProductType.FROZEN

    if (submitData.mealPlan) {
      proteinSelectionFix = submitData.mealPlan.selectedProteins.filter((protein: Proteins) => {
        return !submitData.allergies?.includes(protein)
      })

      if (proteinSelectionFix.length === 0) {
        const availableProteinsBasedOnAllergies = findAvailableProteinsBasedOnAllergies(
          submitData?.allergies || []
        )
        proteinSelectionFix = [availableProteinsBasedOnAllergies[0]]
      }

      if (isFreezeDried(submitData)) {
        const allowedProteins = [Proteins.BEEF, Proteins.CHICKEN]
        proteinSelectionFix = allowedProteins.filter(
          (protein) => !submitData.allergies?.includes(protein)
        )
        initialRawFeedingProductType = RawFeedingProductType.FREEZE_DRIED
      }

      submitData.mealPlan = {
        ...submitData.mealPlan,
        selectedProteins: proteinSelectionFix,
        rawDietPercentage: 100,
        rawFeedingProductType:
          submitData.mealPlan.rawFeedingProductType ?? initialRawFeedingProductType,
      }
    }

    const directToFlexBox = flexBox || flexBoxOption === 'flexBox'

    setPopupOpen('cv-1-2-popup-open')

    const updatedSubmitData = determineFeedingProductType(submitData)

    submitPetAttempt(
      {
        ...updatedSubmitData,
        utmCampaign: Object.keys(router.query).length > 0 ? router.query : undefined,
        creationConfiguration,
        tags: directToFlexBox ? [BoxType.FLEX_BOX] : [],
        boxId: !directToFlexBox ? null : undefined,
        boxPlanId: !directToFlexBox ? null : undefined,
        promoCode: Storage.get(StorageKeys.PROMO_CODE) ?? undefined,
      } as Required<PetAttemptStep3 & Pick<PetAttempt, 'utmCampaign'>>,
      {
        onSuccess: (data) => {
          if (allergiesTooStrict(data.allergies)) return
          if (!emailCheck?.isRegistered || client?.email) {
            setTimeout(
              function () {
                router.push('/meal-plan/step-4')
              },
              ENV.isStaging ? 1000 : 5000
            )
            fbConversion('Register', data)
            return
          }
          router.push('/meal-plan/login')
        },
        onError: (err) => {
          console.log(err)
        },
      }
    )
  }

  const allergiesTooStrict = (allergies: any) => {
    if (!allergies || allergies.length == 0) return false

    let calculatedAllergies = allergies

    const tooStrict = findAvailableProteinsBasedOnAllergies(calculatedAllergies).length === 0

    if (tooStrict) {
      addToast({
        id: 'strict-allergy-error',
        title: 'Allergy selection too strict',
        message:
          "It looks like we couldn't find any proteins that work with your current allergy selection. Please modify to continue.",
        duration: 8000,
        tone: 'error',
        onClose: () => removeToast('strict-allergy-error'),
      })
    }

    return tooStrict
  }

  const findAvailableProteinsBasedOnAllergies = (allergies: Proteins[]) => {
    if (!allergies || allergies.length === 0) return []

    let calculatedAllergies = allergies

    if (allergies.includes(Proteins.TURKEY)) {
      calculatedAllergies = Array.from(new Set([...calculatedAllergies, Proteins.DUCK]))
    }

    if (allergies.includes(Proteins.BEEF)) {
      calculatedAllergies = Array.from(
        new Set([...calculatedAllergies, Proteins.LAMB, Proteins.VENISON])
      )
    }
    const baseProteins = Object.values(Proteins)

    const personalizedRecipesNames = baseProteins.filter((protein) => {
      return !calculatedAllergies?.includes(protein)
    })

    return personalizedRecipesNames
  }

  const handleAllergies = (value: string) => {
    if (value === 'no') {
      setAllergySelected(true)
      setAllergyInfo({ ...allergyInfo, hasAllergies: false })
      setStep3({ ...step3, allergies: [] })
      return
    }
    setAllergyInfo({ ...allergyInfo, hasAllergies: true })
  }

  const allergieDefaultValue = () => {
    if (step3.allergies?.length === 0) {
      return 'no'
    }
    return 'yes'
  }

  const addHealthGoal = (id: any) => {
    const goal = id
    const getHealthGoals = () => {
      if (!step3.healthGoals) {
        return [goal]
      }

      if (step3.healthGoals.includes(goal)) {
        return [...step3.healthGoals]
      }

      return [...step3.healthGoals, goal]
    }

    setStep3({
      ...step3,
      healthGoals: getHealthGoals(),
    })
    setSelectedHealthGoals(
      healthGoalCheckboxes.filter((goal) => getHealthGoals().includes(goal.id)) as any[]
    )
  }

  const handleRemove = (id: string) => {
    const getHealthGoals = () => {
      return Array.isArray(step3?.healthGoals)
        ? step3.healthGoals.filter((goal: any) => goal !== id)
        : []
    }

    setStep3({
      ...step3,
      healthGoals: getHealthGoals(),
    })
    setSelectedHealthGoals(
      healthGoalCheckboxes.filter((goal) => getHealthGoals().includes(goal.id)) as any[]
    )
  }

  const handleReorder = (goals: any[]) => {
    setStep3({
      ...step3,
      healthGoals: goals.map((goal) => goal.id),
    })
    setSelectedHealthGoals(goals)
  }

  type DietType = {
    name: string
    value: DietPrior
  }

  const diets: DietType[] = [
    { name: 'Raw Food', value: DietPrior.FRESH_RAW_FOOD },
    { name: 'Freeze Dried Raw Food', value: DietPrior.FREEZE_DRIED_RAW_FOOD },
    { name: 'Wet Food', value: DietPrior.WET_FOOD },
    { name: 'Cooked Food', value: DietPrior.COOKED_FOOD },
    { name: 'Air Dried Food', value: DietPrior.AIR_DRIED_FOOD },
    { name: 'Kibble', value: DietPrior.KIBBLE },
    { name: 'Other', value: DietPrior.OTHER },
  ]

  return (
    <MealPlanLayout step={3}>
      <LoadingInterstitial petName={petName} popupOpen={popupOpen} />
      <Step3Root>
        <MealPlanQuestionnaireCard>
          <MealPlanFormContainer>
            <form onSubmit={handleSubmit}>
              <Stack
                width="fullWidth"
                direction="column"
                alignItems="center"
                gap={{ '@initial': '4', '@bp2': '7' }}
              >
                <Stack
                  width="fullWidth"
                  direction="column"
                  alignItems="center"
                  gap={{ '@initial': '4', '@bp2': '7' }}
                >
                  <Text
                    size={{ '@initial': 'h3', '@bp2': 'h1' }}
                    font="display"
                    as="h1"
                    weight="semiBold"
                    color="accent-black"
                  >
                    {t('meal-plan:step-3.step-name')}
                  </Text>
                  <Stack direction="column" gap="2" css={{ width: '100%' }}>
                    <RadioGroup.Root
                      pill
                      direction="column"
                      name="hasAllergies"
                      defaultValue={allergieDefaultValue()}
                      onValueChange={handleAllergies}
                      label={t('meal-plan:step-3.allergies', { petName })}
                    >
                      <Stack
                        width="fullWidth"
                        direction="column"
                        css={{ flexWrap: 'nowrap' }}
                        gap="3"
                      >
                        <TextRadioOption value={'yes'} label={t(`common:yes`)} />
                        <TextRadioOption value={'no'} label={t(`common:no`)} />
                      </Stack>
                    </RadioGroup.Root>

                    {(allergyInfo.hasAllergies ||
                      (!allergyInfo.initialLoadFinished && allergieDefaultValue() === 'yes')) && (
                      <>
                        <Text
                          size="headline"
                          color="accent-black"
                          weight="semiBold"
                          as="label"
                          align="center"
                        >
                          {t('meal-plan:step-3.health-goals-helper-text', { petName })}
                        </Text>
                        <Stack
                          direction={{ '@initial': 'column', '@bp2': 'row' }}
                          justifyContent="center"
                          width="fullWidth"
                          gap="2"
                        >
                          <Stack gap="2" direction="column" width="fullWidth">
                            <Checkbox
                              label={t('meal-plan:step-3.beef')}
                              name="BEEF"
                              onChange={handleAllergiesCheckbox}
                              checked={step3.allergies?.includes(Proteins.BEEF)}
                              customCSS={styledCheckbox}
                              labelIcon={{ ...cowIcon, height: '40px', width: '40px' }}
                            />
                            <Checkbox
                              label={t('meal-plan:step-3.chicken')}
                              name="CHICKEN"
                              onChange={handleAllergiesCheckbox}
                              checked={step3.allergies?.includes(Proteins.CHICKEN)}
                              customCSS={styledCheckbox}
                              labelIcon={{ ...chickenIcon, height: '40px', width: '40px' }}
                            />
                            <Checkbox
                              label={t('meal-plan:step-3.duck')}
                              name="DUCK"
                              onChange={handleAllergiesCheckbox}
                              checked={step3.allergies?.includes(Proteins.DUCK)}
                              customCSS={styledCheckbox}
                              labelIcon={{ ...duckIcon, height: '40px', width: '40px' }}
                            />
                          </Stack>
                          <Stack gap="2" width="fullWidth" direction="column">
                            <Checkbox
                              label={t('meal-plan:step-3.lamb')}
                              name="LAMB"
                              onChange={handleAllergiesCheckbox}
                              checked={step3.allergies?.includes(Proteins.LAMB)}
                              customCSS={styledCheckbox}
                              labelIcon={{ ...lambIcon, height: '40px', width: '40px' }}
                            />
                            <Checkbox
                              label={t('meal-plan:step-3.turkey')}
                              name="TURKEY"
                              onChange={handleAllergiesCheckbox}
                              checked={step3.allergies?.includes(Proteins.TURKEY)}
                              customCSS={styledCheckbox}
                              labelIcon={{ ...turkeyIcon, height: '40px', width: '40px' }}
                            />
                            <Checkbox
                              label={t('meal-plan:step-3.venison')}
                              name="VENISON"
                              onChange={handleAllergiesCheckbox}
                              checked={step3.allergies?.includes(Proteins.VENISON)}
                              customCSS={styledCheckbox}
                              labelIcon={{ ...venisonIcon, height: '40px', width: '40px' }}
                            />
                          </Stack>
                        </Stack>
                      </>
                    )}
                  </Stack>

                  {/* health goals */}
                  <Stack direction="column" gap="2" css={{ width: '100%', maxWidth: '640px' }}>
                    <Stack direction="column" gap="1">
                      <Text
                        size="headline"
                        color="accent-black"
                        weight="semiBold"
                        as="label"
                        align="center"
                      >
                        <Stack>
                          <div>
                            <Trans
                              i18nKey="meal-plan:step-3.health-benefits-optional"
                              components={{
                                text: <Text display="inline" size="small" />,
                              }}
                            />
                          </div>
                          <Tooltip
                            instant
                            side="top"
                            text={t('meal-plan:step-3.health-goals-tooltip')}
                          >
                            <Box css={{ display: 'flex', alignItems: 'center' }}>
                              <Icon name="information-circle-line" color="accent-black" />
                            </Box>
                          </Tooltip>
                        </Stack>
                      </Text>
                    </Stack>
                    <Stack direction="column">
                      <Stack direction="column" gap="2">
                        {step3 &&
                          healthGoalCheckboxes.map(
                            (goal, i) =>
                              !step3.healthGoals?.includes(goal.id) && (
                                <BenefitsBox
                                  key={`${i}_${goal.name}`}
                                  onClick={() => addHealthGoal(goal.id)}
                                  direction="row"
                                  alignItems="center"
                                  justifyContent="flex-start"
                                >
                                  <BenefitsBoxPlusIcon justifyContent="flex-start">
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="18"
                                      height="18"
                                      viewBox="0 0 18 18"
                                      fill="none"
                                    >
                                      <path
                                        d="M9 3.75V14.25M3.75 9H14.25"
                                        stroke="#EB3A26"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  </BenefitsBoxPlusIcon>
                                  <div>{goal.label}</div>
                                </BenefitsBox>
                              )
                          )}
                      </Stack>
                    </Stack>
                    <Stack direction="column" gap="2">
                      {selectedHealthGoals && selectedHealthGoals.length > 0 && (
                        <>
                          <Stack>Chosen Benefits</Stack>
                          <RankedDragAndDropList
                            items={selectedHealthGoals}
                            onChange={handleReorder}
                            rankItemsLimit={selectedHealthGoals.length}
                            renderItem={(item: any, i: number) => (
                              <RankedDragAndDropList.Item id={item.id} key={item.id}>
                                <RankedDragAndDropList.DragHandle />
                                <Stack alignItems="center" css={{ width: 'calc(100% - 55px)' }}>
                                  {item.label}
                                  {i === 0 && <PrimaryGoal> Primary Goal</PrimaryGoal>}
                                </Stack>
                                <RemoveButtonWrapper onClick={() => handleRemove(item.id)}>
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="18"
                                    height="18"
                                    viewBox="0 0 18 18"
                                    fill="none"
                                  >
                                    <path
                                      d="M13.5 4.5L4.5 13.5M4.5 4.5L13.5 13.5"
                                      stroke="#848484"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </RemoveButtonWrapper>
                              </RankedDragAndDropList.Item>
                            )}
                          />
                        </>
                      )}
                    </Stack>
                  </Stack>

                  {/* eating habits  */}
                  <RadioGroup.Root
                    direction="column"
                    pill
                    name="eatingHabits"
                    defaultValue={step3.eatingHabits}
                    onValueChange={(eatingHabits) =>
                      setStep3({
                        ...step3,
                        eatingHabits: eatingHabits as EatingHabits,
                      })
                    }
                    label={t('meal-plan:step-3.how-picky', { petName })}
                    css={{
                      width: '100%',
                      '& button': {
                        width: '100%',
                      },
                    }}
                  >
                    <Stack width="fullWidth" direction="column" gap="3">
                      <TextRadioOption
                        value={EatingHabits.PICKY_EATER}
                        label={t('meal-plan:step-3.picky')}
                        css={{ fontWeight: '400' }}
                      />
                      <TextRadioOption
                        value={EatingHabits.GOOD_EATER}
                        label={t('meal-plan:step-3.good-eater')}
                      />
                      <TextRadioOption
                        value={EatingHabits.WILL_EAT_ANYTHING}
                        label={t('meal-plan:step-3.eat-anything')}
                      />
                    </Stack>
                  </RadioGroup.Root>

                  <Stack direction="column" width="fullWidth" gap="2">
                    <Text
                      size="headline"
                      color="accent-black"
                      weight="semiBold"
                      as="label"
                      align="center"
                    >
                      {t('meal-plan:step-3.diet-prior', { petName })}
                    </Text>
                    <Select<DietType>
                      id="dietPrior"
                      defaultValue={diets.find((diet) => diet.value === step3.dietPrior)}
                      name="dietPrior"
                      items={diets}
                      label={'Select'}
                      onChange={(_, selectedDiet) => {
                        setStep3({
                          ...step3,
                          dietPrior: selectedDiet?.value,
                        })
                      }}
                      itemToString={(item) => (item ? item.name : '')}
                      displayformatter={(diet) => diet.name}
                      optionFormatter={(diet) => diet.name}
                    />
                  </Stack>

                  {flexBoxButtons && (
                    <RadioGroup.Root
                      direction="row"
                      name="flexBox"
                      defaultValue={flexBoxOption || 'recommended'}
                      onValueChange={(option: string) => setFlexBoxOption(option)}
                      label={t('meal-plan:step-3.what-are-you-looking-for')}
                    >
                      <Stack direction="row" css={{ flexWrap: 'nowrap' }}>
                        <TextRadioOption
                          value={'recommended'}
                          label={t(`meal-plan:step-3.recommended-meal-plan`)}
                        />
                        <TextRadioOption
                          value={'flexBox'}
                          label={t(`meal-plan:step-3.build-your-own-box`)}
                        />
                      </Stack>
                    </RadioGroup.Root>
                  )}
                </Stack>
                <MealPlanSubscription
                  direction="column"
                  alignItems="center"
                  gap={{ '@initial': '3', '@bp2': '3' }}
                >
                  <TitleContainer
                    direction="column"
                    alignItems="center"
                    gap={{ '@initial': '2', '@bp2': '3' }}
                  >
                    <Text
                      as="h3"
                      size={{ '@initial': 'h3', '@bp2': 'h1' }}
                      weight="bold"
                      color="accent-black"
                    >
                      {t('meal-plan:step-3.subscription-title')}
                    </Text>
                  </TitleContainer>

                  <EmailContainer
                    direction="column"
                    gap={{ '@initial': '7', '@bp2': '6' }}
                    css={{ maxWidth: '600px' }}
                  >
                    <Stack direction="column" gap="2">
                      <TextInput
                        type="email"
                        label={t('meal-plan:step-3.email')}
                        placeholder={t('meal-plan:step-3.email')}
                        defaultValue={client?.email || step3.email?.toLowerCase()}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          onEmailChange(e.target.value.toLowerCase())
                        }
                        readOnly={Boolean(client?.email)}
                      />
                      {/* SMS CONSENT */}

                      <TextInput
                        type="tel"
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          onPhoneNumberChange(e.target.value)
                        }
                        outerLabelGap="1"
                        id="phoneNumber"
                        name="phone-Number"
                        label={t('checkout-form:phone')}
                        placeholder="phone number (optional)"
                        maxLength={10}
                        value={client?.phoneNumber || step3.phoneNumber || ''}
                        readOnly={Boolean(client?.phoneNumber)}
                      />

                      <Stack gap="0" css={{ width: '100%' }}>
                        <Checkbox
                          label={t('meal-plan:step-3.terms-label')}
                          checked={termsAndConditionsAcceptance}
                          onChange={(e) => setTermsAndConditionsAcceptance(e.target.checked)}
                          hiddenLabel
                          required
                          customCSS={{ alignItems: 'flex-start' }}
                        />
                        <Text size="small" as="span" aria-hidden>
                          {client?.phoneNumber || step3.phoneNumber ? (
                            <TermsAndConditionsWrapper
                              onClick={(e) =>
                                setTermsAndConditionsAcceptance(!termsAndConditionsAcceptance)
                              }
                            >
                              <Trans
                                i18nKey="meal-plan:step-3.terms-with-sms"
                                components={{
                                  link: (
                                    <Text
                                      as="a"
                                      href="/terms-and-conditions"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      size="small"
                                      color="accent-black"
                                      underline
                                    />
                                  ),
                                  link1: (
                                    <Text
                                      underline
                                      as="a"
                                      href="/privacy-policy"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      size="small"
                                      color="accent-black"
                                    />
                                  ),
                                }}
                              />
                            </TermsAndConditionsWrapper>
                          ) : (
                            <TermsAndConditionsWrapper
                              onClick={(e) =>
                                setTermsAndConditionsAcceptance(!termsAndConditionsAcceptance)
                              }
                            >
                              <Trans
                                i18nKey="meal-plan:step-3.terms"
                                components={{
                                  link: (
                                    <Text
                                      underline
                                      as="a"
                                      href="/terms-and-conditions"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      size="small"
                                      color="accent-black"
                                    />
                                  ),
                                }}
                              />
                            </TermsAndConditionsWrapper>
                          )}
                        </Text>
                      </Stack>
                    </Stack>

                    <CTAContainer direction="column" alignItems="center" gap="4">
                      <Button size="regular" fullWidth disabled={!allFieldsFilled}>
                        {t('meal-plan:step-3.cta')}
                      </Button>
                      <Link href="/meal-plan/step-2" passHref>
                        <Text as="a" weight={{ '@initial': 'bold', '@bp1': 'regular' }}>
                          {t('common:go-back')}
                        </Text>
                      </Link>
                    </CTAContainer>
                  </EmailContainer>
                </MealPlanSubscription>
              </Stack>
            </form>
          </MealPlanFormContainer>
        </MealPlanQuestionnaireCard>
      </Step3Root>
      <FooterMessage
        imageAlt="meal-plan:step-3.footer.imageAlt"
        imageSrc={smallDog}
        subtext="meal-plan:step-3.footer.subtext"
        title="meal-plan:step-3.footer.title"
      />
    </MealPlanLayout>
  )
}

Step3.displayName = 'Step3'

const styledCheckbox = {
  padding: '7px 16px',
  gap: '0px',
  background: '$accent-white',
  border: '1px solid $accent-gray',
  borderRadius: '8px',
  width: '100%',

  '& > label > div': {
    border: 'none',
    fontSize: '$body',
  },

  '&:has(input:checked)': {
    background: '$tertiary-200',
    border: '1px solid $primary-500',

    '& > label > div': {
      background: '$tertiary-200',
    },
  },
}
